// The one chart theme of the web app (the graph design language of the
// density spec).
//
// Each colour is a CSS variable. A recharts SVG presentation attribute
// resolves `rgb(var(--x))`, so dark mode changes the chart with no re-render.
// Do not put a hex literal in this directory: the guard test fails the build
// for one.

import { formatCompactGBP } from '@/lib/format';

/** Make a colour string from a design-token RGB triplet. */
function tokenColour(token: string, alpha?: number): string {
    return alpha === undefined ? `rgb(var(--${token}))` : `rgb(var(--${token}) / ${alpha})`;
}

/** The palette roles. One colour carries meaning; grey carries context. */
export const CHART = {
    primary: tokenColour('color-accent'),
    grid: tokenColour('color-divider'),
    axis: tokenColour('color-text-tertiary'),
    zero: tokenColour('color-text-tertiary'),
    surface: tokenColour('color-surface'),
    textPrimary: tokenColour('color-text-primary'),
    textSecondary: tokenColour('color-text-secondary'),
    barCursor: tokenColour('color-text-primary', 0.04),
} as const;

/** The named tones that a caller can give instead of a colour string. */
export type ChartTone =
    | 'primary'
    | 'comparison'
    | 'positive'
    | 'negative'
    | 'limit'
    | 'agent'
    | 'chart-1'
    | 'chart-2'
    | 'chart-3'
    | 'chart-4'
    | 'chart-5'
    | 'chart-6';

export function toneColour(tone: ChartTone, alpha?: number): string {
    switch (tone) {
        case 'primary':
            return tokenColour('color-accent', alpha);
        case 'comparison':
            return tokenColour('color-text-tertiary', alpha);
        case 'positive':
            return tokenColour('color-rag-green', alpha);
        case 'negative':
            return tokenColour('color-rag-red', alpha);
        case 'limit':
            return tokenColour('color-rag-amber', alpha);
        case 'agent':
            return alpha === undefined ? 'rgb(var(--agent))' : `rgb(var(--agent) / ${alpha})`;
        default:
            return tokenColour(`color-${tone}`, alpha);
    }
}

/** Categorical tones in order. A 7th category and after folds into "Other". */
const CATEGORICAL_TONES: readonly ChartTone[] = [
    'chart-1',
    'chart-2',
    'chart-3',
    'chart-4',
    'chart-5',
    'chart-6',
];

/** The tone of the category at `index`. The last tone is the "Other" tone. */
export function categoricalTone(index: number): ChartTone {
    return CATEGORICAL_TONES[Math.min(index, CATEGORICAL_TONES.length - 1)]!;
}

/** The fixed tone of each coarse asset class (see server classifyAssetClass). */
const ASSET_CLASS_TONES: Record<string, ChartTone> = {
    Equity: 'chart-1',
    Fund: 'chart-2',
    'Fixed Income': 'chart-4',
    'Investment Trust': 'chart-3',
    Cash: 'chart-6',
};

/** An unknown label must still show, so it gets the "Other" tone. */
export function assetClassTone(label: string): ChartTone {
    return ASSET_CLASS_TONES[label] ?? 'chart-6';
}

/** Tone by sign: positive for zero and above, negative below zero. */
export function signTone(value: number): ChartTone {
    return value >= 0 ? 'positive' : 'negative';
}

// --- Size classes -----------------------------------------------------------

/**
 * The chart heights in px. `auto` is for a chart that sets its own pixel
 * height from its row count.
 */
export type ChartSize = 'compact' | 'standard' | 'hero' | 'auto';

/** Tailwind height classes for each size (the frame never jumps). */
export const SIZE_CLASS: Record<ChartSize, string> = {
    compact: 'h-[120px]',
    standard: 'h-[192px]',
    hero: 'h-[272px]',
    auto: 'min-h-[120px]',
};

/** The Y tick count for each size. */
export const TICK_COUNT: Record<ChartSize, number> = {
    compact: 3,
    standard: 4,
    hero: 5,
    auto: 4,
};

export const MARGIN = { top: 8, right: 8, bottom: 0, left: 0 } as const;
/** The margin when end tags sit to the right of the plot. */
export const MARGIN_END_TAGS = { top: 8, right: 52, bottom: 0, left: 0 } as const;

// --- Axes, grid, marks ------------------------------------------------------

/** A numeric tick. The mono face comes from the frame class (see ChartFrame). */
export const NUMERIC_TICK = { fontSize: 10.5, fill: CHART.axis } as const;
/** A word tick (a category or a date). */
export const WORD_TICK = { fontSize: 10.5, fill: CHART.axis } as const;

/** The shared axis props: no axis line, no tick line, a 6px tick margin. */
export const AXIS_BASE = { axisLine: false, tickLine: false, tickMargin: 6 } as const;

/**
 * The X axis of a time axis: never rotated, never crowded. It drops ticks in
 * the middle and keeps the first and the last. Do not use it for a category
 * axis (see `X_CATEGORY_AXIS`).
 */
export const X_WORD_AXIS = {
    ...AXIS_BASE,
    tick: WORD_TICK,
    interval: 'preserveStartEnd',
    minTickGap: 28,
} as const;

/**
 * The X axis of a category axis. It shows each category (interval 0). A label
 * that is wider than its slot gets a shorter text, and it is never dropped.
 */
export const X_CATEGORY_AXIS = {
    ...AXIS_BASE,
    tick: WORD_TICK,
    interval: 0,
} as const;

export const LINE_CURSOR = {
    stroke: CHART.axis,
    strokeWidth: 1,
    strokeDasharray: '2 2',
} as const;
export const BAR_CURSOR = { fill: CHART.barCursor } as const;

/** Tooltip timing: the tooltip is the only thing that animates. */
export const TOOLTIP_ANIMATION_MS = 100;

// --- Number shapes ----------------------------------------------------------

const MINUS = '−';

/** Replace a leading ASCII hyphen-minus with the true minus sign (U+2212). */
export function withTrueMinus(text: string): string {
    return text.startsWith('-') ? `${MINUS}${text.slice(1)}` : text;
}

/** Money on an axis or a tag: `£1.2M`, `£250K`, `−£5K`. */
export function formatAxisMoney(value: number): string {
    return withTrueMinus(formatCompactGBP(value));
}

/** A plain count on an axis: `1,240`. */
export function formatAxisCount(value: number): string {
    return withTrueMinus(Math.round(value).toLocaleString('en-GB'));
}

const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
] as const;

/**
 * A `YYYY-MM` month tick: `Mar 26`. Each tick has the same format, so the
 * reader never sees `Apr 26` beside `May`. Passes other text through.
 */
export function formatMonthTick(month: string): string {
    const [y, m] = month.split('-');
    const idx = Number(m) - 1;
    if (!y || !m || idx < 0 || idx > 11) return month;
    return `${MONTHS_SHORT[idx]!} ${y.slice(2)}`;
}

const NICE_STEPS = [1, 2, 2.5, 5, 10] as const;

/** The next step in the 1, 2, 2.5, 5, 10 scale that is larger than `step`. */
function nextNiceStep(step: number): number {
    const power = 10 ** Math.floor(Math.log10(step));
    for (const n of NICE_STEPS) if (n * power > step * 1.0000001) return n * power;
    return 10 * power;
}

function niceStepAtLeast(raw: number): number {
    const power = 10 ** Math.floor(Math.log10(raw));
    for (const n of NICE_STEPS) if (n * power >= raw * 0.9999999) return n * power;
    return 10 * power;
}

export interface AxisTicks {
    ticks: number[];
    domain: [number, number];
}

/**
 * Ticks for a numeric axis whose labels are all different. It starts from a
 * nice step for about `count` ticks. If two ticks would give the same label,
 * it uses a larger step. A flat series gets one tick.
 */
export function distinctAxisTicks(
    values: readonly number[],
    count: number,
    format: (value: number) => string,
): AxisTicks | null {
    const finite = values.filter((v) => Number.isFinite(v));
    if (finite.length === 0) return null;
    const lo = Math.min(...finite);
    const hi = Math.max(...finite);
    if (lo === hi) {
        const pad = Math.abs(lo) * 0.05 || 1;
        return { ticks: [lo], domain: [lo - pad, hi + pad] };
    }
    let step = niceStepAtLeast((hi - lo) / Math.max(1, count - 1));
    for (let guard = 0; guard < 40; guard++) {
        const first = Math.floor(lo / step) * step;
        const last = Math.ceil(hi / step) * step;
        const ticks: number[] = [];
        for (let t = first; t <= last + step / 2; t += step) {
            ticks.push(Math.round(t / step) * step);
        }
        const labels = new Set(ticks.map(format));
        if (labels.size === ticks.length) {
            return { ticks, domain: [ticks[0]!, ticks[ticks.length - 1]!] };
        }
        step = nextNiceStep(step);
    }
    return null;
}

/** Shorten a long category label to fit the category axis. */
export function truncateLabel(label: string, max = 22): string {
    return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

// The width of a tick label. In the browser a canvas measures the text in the
// face of the page. Where no canvas is available (the server render and jsdom
// in the tests), an estimate from the character classes gives the width. The
// estimate is a little high on purpose: a label that is too short is better
// than a label that is clipped.
const WIDE_CHARS = /[A-Z&@%MWmw]/;
const NARROW_CHARS = /[-iljtfrI.,:;'|!() ]/;

function estimateEm(text: string): number {
    let em = 0;
    for (const ch of text) {
        if (WIDE_CHARS.test(ch)) em += 0.75;
        else if (NARROW_CHARS.test(ch)) em += 0.32;
        else em += 0.6;
    }
    return em;
}

let measureContext: CanvasRenderingContext2D | null | undefined;
let measureFamily = 'sans-serif';

function canvasContext(): CanvasRenderingContext2D | null {
    if (measureContext !== undefined) return measureContext;
    measureContext = null;
    if (typeof document === 'undefined' || typeof navigator === 'undefined') return null;
    // jsdom has a canvas element with no 2D context, and it logs an error
    // when a test asks for one.
    if (/jsdom/i.test(navigator.userAgent)) return null;
    try {
        measureContext = document.createElement('canvas').getContext('2d');
        measureFamily = getComputedStyle(document.body).fontFamily || measureFamily;
    } catch {
        measureContext = null;
    }
    return measureContext;
}

/** The width in px of `text` in the tick face. */
export function estimateTextWidth(text: string, fontSize = WORD_TICK.fontSize): number {
    const ctx = canvasContext();
    if (ctx) {
        ctx.font = `${fontSize}px ${measureFamily}`;
        return ctx.measureText(text).width;
    }
    return estimateEm(text) * fontSize;
}

/**
 * Shorten `label` from the end, with an ellipsis, so it fits in `maxWidth` px.
 * A label that fits is not changed. The start of a label always shows.
 */
export function fitLabel(label: string, maxWidth: number, fontSize = WORD_TICK.fontSize): string {
    if (estimateTextWidth(label, fontSize) <= maxWidth) return label;
    const chars = Array.from(label);
    let end = chars.length - 1;
    while (end > 1) {
        const candidate = `${chars.slice(0, end).join('').trimEnd()}…`;
        if (estimateTextWidth(candidate, fontSize) <= maxWidth) return candidate;
        end -= 1;
    }
    return `${chars[0] ?? ''}…`;
}

/** A symmetric domain around zero, for a diverging chart. */
export function symmetricDomain(values: readonly number[]): [number, number] {
    const most = values.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
    const edge = most === 0 ? 1 : most;
    return [-edge, edge];
}
